import React, { useMemo, useState, useEffect } from 'react';
import { GameState } from '../../constants/types';
import { commonStyles, narrativeUIStyles } from '../../constants/styles';
import { useNarrativeLogic } from '../data/NarrativeUI';
import { useTheme } from '../../services/ThemeContext';
import { useSettings } from '../../services/SettingsContext';
import { isWordStudyTopic, getRandomLoadingMessage } from '../../constants/variables';
import FeedbackModal from './FeedbackModal';
import SettingsIconButton from './Helpers/SettingsIconButton.web';

/**
 * NarrativeUI.web.tsx
 *
 * PLATFORM: Web
 *
 * Key differences from native:
 * - Sidebar is inline on large screens (!isSmallScreen)
 * - Sidebar is modal overlay on small screens (isSmallScreen)
 * - Responsive behavior based on screen width
 */

interface NarrativeUIProps {
  gameState: GameState;
  onSendMessage: (text: string) => void;
  onSilentContinue: () => void;
  onRedo: () => void;
  onExit: () => void;
  isLoading: boolean;
  onDonate: () => void;
  onOpenSettings: () => void;
}

const NarrativeUI: React.FC<NarrativeUIProps> = props => {
  const { currentTheme: colors, fontFamily } = useTheme();
  const { transparencySettings } = useSettings();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [showChoicesDropdown, setShowChoicesDropdown] = useState(false);
  const [showPassagesDropdown, setShowPassagesDropdown] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Crafting your story...');
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const wasLoadingRef = React.useRef(false);

  const { inputText, setInputText, handleSend, handleKeyPress } = useNarrativeLogic({
    gameState: props.gameState,
    onSendMessage: props.onSendMessage,
    onSilentContinue: props.onSilentContinue,
    isLoading: props.isLoading,
    isDebug: false,
  });

  // Update loading message when loading starts
  useEffect(() => {
    if (props.isLoading && !wasLoadingRef.current) {
      setLoadingMessage(getRandomLoadingMessage());
    }
    wasLoadingRef.current = props.isLoading;
  }, [props.isLoading]);

  const {
    gameState,
    onSilentContinue,
    onRedo,
    onExit,
    isLoading,
    onSendMessage,
    onDonate,
    onOpenSettings,
  } = props;

  const handleToggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const handleSaveAndExit = () => {
    onExit();
  };

  const handleAbandonStory = () => {
    onExit();
  };

  const lastMessage =
    gameState?.history?.length > 0 ? gameState.history[gameState.history.length - 1] : undefined;
  const actions = useMemo(
    () => (lastMessage?.suggestedActions || []).slice(0, 2),
    [lastMessage?.suggestedActions]
  );
  const relatedPassages = useMemo(
    () => lastMessage?.relatedPassages || [],
    [lastMessage?.relatedPassages]
  );
  const isWordStudy = useMemo(
    () => gameState?.bibleVerse ? isWordStudyTopic(gameState.bibleVerse) : false,
    [gameState?.bibleVerse]
  );
  const choicesWithAsk = useMemo(
    () => [...actions, '❓ Ask what this means'],
    [actions]
  );

  const handleScrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    handleScrollToBottom();
  }, [gameState?.history?.length, isLoading]);

  const spinnerStyle: React.CSSProperties = {
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid currentColor',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    animation: 'spin 1s linear infinite',
    padding: '4px',
  };

  return (
    <div style={{ flex: 1, position: 'relative', maxWidth: '500px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Main content with transparency overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: `rgba(0, 0, 0, ${transparencySettings.textAreaOpacity / 100})`,
            zIndex: 0,
          }}
        >
          {/* MAIN CONTENT AREA */}
          <div style={commonStyles.contentArea}>
            {/* Header Row - Menu, Title, Settings */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: colors.surface,
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
                borderBottomStyle: 'solid',
              }}
            >
              {/* Menu Button - Left */}
              <button
                data-testid="narrative-menu-button"
                onClick={handleToggleSidebar}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <span style={{ fontSize: 20, color: colors.primary }}>☰</span>
              </button>

              {/* Title - Center - Shows Scripture/phrase prompt */}
              <p
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: colors.primary,
                  margin: 0,
                  textAlign: 'center',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {gameState.bibleVerse}
              </p>

              {/* Settings - Right */}
              <SettingsIconButton onClick={onOpenSettings} />
            </div>

            <div
              ref={scrollRef}
              style={{ ...commonStyles.narrativeScroll, paddingBottom: 140 }}
            >
              <div style={commonStyles.narrativeContent}>
                {(gameState?.history || []).map((m, i) => (
                  <div
                    key={m.id || i}
                    style={{
                      ...commonStyles.messageContainer,
                      ...(m.role === 'user' ? commonStyles.userAlign : commonStyles.modelAlign),
                    }}
                  >
                    <div
                      style={{
                        ...(m.role === 'user' ? commonStyles.userBubble : commonStyles.modelBubble),
                        backgroundColor:
                          m.role === 'user' ? `${colors.primary}1A` : 'transparent',
                        borderColor: m.role === 'user' ? `${colors.primary}4D` : 'transparent',
                        borderStyle: 'solid',
                      }}
                    >
                      <p
                        style={{
                          ...commonStyles.messageText,
                          color: colors.textMain,
                          fontWeight: '500',
                          margin: 0,
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          fontFamily: fontFamily,
                          fontSize: 'var(--app-font-size)',
                        }}
                      >
                        {m.content}
                      </p>
                      {m.imageUrl && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginVertical: 8,
                            width: '100%',
                          }}
                        >
                          <img
                            src={m.imageUrl}
                            alt="Scene"
                            style={{ ...narrativeUIStyles.sceneImage, objectFit: 'contain' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 20 }}>
                    <div style={{ ...spinnerStyle, color: colors.textMuted }}></div>
                    <p style={{
                      color: colors.textMuted,
                      fontStyle: 'italic',
                      marginLeft: 8,
                      margin: 0,
                      fontFamily: fontFamily,
                      fontSize: 'var(--app-font-size)',
                    }}>
                      {loadingMessage}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Bar & Input Footer */}
            <div
              style={{
                ...commonStyles.footer,
                backgroundColor: `${colors.background}E6`,
                borderTopColor: colors.border,
                borderTopStyle: 'solid',
                borderTopWidth: 1,
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              {/* Dropdowns Row - Related Passages, Choices, and Redo */}
              {!isLoading && (choicesWithAsk.length > 0 || (isWordStudy && relatedPassages.length > 0)) && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 12,
                    gap: 8,
                  }}
                >
                  {/* Related Passages Dropdown Button - ONLY for word studies */}
                  {isWordStudy && relatedPassages.length > 0 && (
                    <button
                      style={{
                        flex: 1,
                        backgroundColor: colors.surface,
                        borderWidth: 1,
                        borderColor: colors.primary,
                        borderRadius: 8,
                        padding: '10px 12px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setShowChoicesDropdown(false);
                        setShowPassagesDropdown(true);
                      }}
                    >
                      <span style={{ color: colors.textMain, fontSize: 14 }}>
                        {lastMessage?.currentPassage || gameState.bibleVerse}
                      </span>
                      <span style={{ color: colors.primary, fontSize: 12 }}>▲</span>
                    </button>
                  )}

                  {/* Choices Dropdown Button */}
                  {choicesWithAsk.length > 0 && (
                    <button
                      style={{
                        flex: 1,
                        backgroundColor: colors.primary,
                        borderWidth: 1,
                        borderColor: colors.primary,
                        borderRadius: 8,
                        padding: '10px 12px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setShowPassagesDropdown(false);
                        setShowChoicesDropdown(true);
                      }}
                    >
                      <span style={{ color: colors.background, fontSize: 14 }}>
                        Choices ({choicesWithAsk.length})
                      </span>
                      <span style={{ color: colors.background, fontSize: 12 }}>▲</span>
                    </button>
                  )}

                  {/* Redo Button */}
                  <button
                    data-testid="narrative-redo"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                    onClick={onRedo}
                    disabled={isLoading || !gameState?.history || gameState.history.length === 0}
                  >
                    <span style={{ color: colors.textMain, fontSize: 13 }}>↺ Redo</span>
                  </button>
                </div>
              )}

              {/* Text Input Area with Continue Button */}
              <div
                style={{
                  ...narrativeUIStyles.inputControlWrapper,
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <textarea
                  data-testid="narrative-input"
                  style={{ ...commonStyles.mainTextInput, color: colors.textMain, flex: 1, resize: 'none' }}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="What do you do? (blank to continue)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const trimmedInput = inputText.trim();
                      if (trimmedInput !== '') {
                        onSendMessage(trimmedInput);
                      } else {
                        onSilentContinue();
                      }
                      setInputText('');
                    }
                  }}
                  rows={2}
                />

                {/* Continue Button */}
                <button
                  data-testid="narrative-continue"
                  style={{
                    ...commonStyles.iconButton,
                    ...narrativeUIStyles.inputControlButton,
                    backgroundColor: colors.primary,
                    borderColor: colors.primary,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    const trimmedInput = inputText.trim();
                    if (trimmedInput !== '') {
                      onSendMessage(trimmedInput);
                    } else {
                      onSilentContinue();
                    }
                    setInputText('');
                  }}
                  disabled={isLoading}
                >
                  <span
                    style={{
                      ...commonStyles.buttonTextSmall,
                      color: colors.isDark ? colors.background : colors.textMain,
                    }}
                  >
                    Continue
                  </span>
                </button>
              </div>

              {/* Dropdowns - Rendered outside footer with fixed positioning */}
              {!isLoading && showPassagesDropdown && relatedPassages.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 100,
                    left: 16,
                    right: 16,
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.primary,
                    borderStyle: 'solid',
                    borderRadius: 8,
                    maxHeight: 200,
                    zIndex: 2000,
                    boxShadow: '0 -4px 8px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderBottomWidth: 1,
                      borderBottomColor: colors.border,
                      borderBottomStyle: 'solid',
                    }}
                  >
                    <p style={{ color: colors.primary, fontSize: 14, fontWeight: '600', margin: 0 }}>
                      Related Passages
                    </p>
                    <button onClick={() => setShowPassagesDropdown(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <span style={{ color: colors.primary, fontSize: 18 }}>✕</span>
                    </button>
                  </div>
                  <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                    {relatedPassages.map((passage, index) => (
                      <button
                        key={index}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderBottomWidth: index < relatedPassages.length - 1 ? 1 : 0,
                          borderBottomColor: colors.border,
                          borderBottomStyle: 'solid',
                          backgroundColor: 'transparent',
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderTop: 'none',
                          borderRadius: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onClick={() => {
                          setShowPassagesDropdown(false);
                          onSendMessage(`Explore ${passage}`);
                        }}
                      >
                        <span style={{ color: colors.textMain, fontSize: 14 }}>{passage}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Choices Dropdown */}
              {!isLoading && showChoicesDropdown && choicesWithAsk.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 100,
                    left: 16,
                    right: 16,
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.primary,
                    borderStyle: 'solid',
                    borderRadius: 8,
                    maxHeight: 200,
                    zIndex: 2000,
                    boxShadow: '0 -4px 8px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderBottomWidth: 1,
                      borderBottomColor: colors.border,
                      borderBottomStyle: 'solid',
                    }}
                  >
                    <p style={{ color: colors.primary, fontSize: 14, fontWeight: '600', margin: 0 }}>
                      Choices
                    </p>
                    <button onClick={() => setShowChoicesDropdown(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <span style={{ color: colors.primary, fontSize: 18 }}>✕</span>
                    </button>
                  </div>
                  <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                    {choicesWithAsk.map((choice, index) => {
                      const isAsk = choice.startsWith('❓');
                      return (
                        <button
                          key={index}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderBottomWidth: index < choicesWithAsk.length - 1 ? 1 : 0,
                            borderBottomColor: colors.border,
                            borderBottomStyle: 'solid',
                            backgroundColor: isAsk ? `${colors.primary}10` : 'transparent',
                            borderLeft: 'none',
                            borderRight: 'none',
                            borderTop: 'none',
                            borderRadius: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                          onClick={() => {
                            setShowChoicesDropdown(false);
                            // Handle the Ask option specially
                            if (isAsk) {
                              onSendMessage('What does this mean?');
                            } else {
                              onSendMessage(choice);
                            }
                          }}
                        >
                          <span
                            style={{
                              color: isAsk ? colors.primary : colors.textMain,
                              fontSize: 14,
                              fontWeight: isAsk ? '600' : 'normal',
                            }}
                          >
                            {choice}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SIDEBAR OVERLAY - Uses conditional rendering instead of Modal for better web support */}
          {sidebarVisible && (
            <>
              {/* Full-screen overlay */}
              <div
                data-testid="sidebar-overlay"
                onClick={handleToggleSidebar}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 9998,
                }}
              />
              {/* Sidebar panel - positioned from left */}
              <div
                data-testid="sidebar-panel"
                style={{
                  ...commonStyles.sidebar,
                  ...narrativeUIStyles.mobileSidebar,
                  backgroundColor: colors.surface,
                  borderRightColor: colors.border,
                  borderRightStyle: 'solid',
                  borderRightWidth: 1,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  zIndex: 9999,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ ...commonStyles.sidebarHeader, borderBottomColor: colors.border, borderBottomStyle: 'solid', borderBottomWidth: 1 }}>
                  <p
                    style={{ ...commonStyles.title, color: colors.primary, fontSize: 24, marginBottom: 10, margin: 0 }}
                  >
                    {gameState.storyName}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ ...commonStyles.label, color: colors.textMuted, margin: 0 }}>
                      {gameState.bibleVerse}
                    </p>
                    <button data-testid="sidebar-close-button" onClick={handleToggleSidebar} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <span style={{ color: colors.textMuted, fontSize: 20 }}>✕</span>
                    </button>
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {/* Donation Button */}
                <button
                  onClick={() => {
                    setSidebarVisible(false);
                    onDonate();
                  }}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: `${colors.primary}15`,
                    borderWidth: 1,
                    borderColor: colors.primary,
                    borderStyle: 'solid',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 24 }}>☕</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 14, fontWeight: 'bold', color: colors.primary }}>
                        Support the Developer
                      </div>
                      <div style={{ fontSize: 12, color: colors.textMuted }}>
                        Help keep this app free
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 24 }}>💜</span>
                </button>

                {/* Feedback Button */}
                <button
                  onClick={() => {
                    setSidebarVisible(false);
                    setShowFeedback(true);
                  }}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 24,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: `${colors.primary}10`,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 24 }}>💬</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 14, fontWeight: 'bold', color: colors.primary }}>
                        Send Feedback
                      </div>
                      <div style={{ fontSize: 12, color: colors.textMuted }}>
                        Help, bug reports, ideas
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 24 }}>→</span>
                </button>

                {/* Directives */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                    Active Directives
                  </div>
                  <textarea
                    value={gameState.directives || ''}
                    readOnly
                    rows={5}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 8,
                      fontSize: 14,
                      backgroundColor: colors.background,
                      borderColor: colors.border,
                      borderStyle: 'solid',
                      color: colors.textMain,
                      height: 120,
                    }}
                  />
                </div>

                {/* Negative Directives */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                    Avoid (Negative Directives)
                  </div>
                  <textarea
                    value={gameState.negativeDirectives || ''}
                    readOnly
                    rows={5}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 8,
                      fontSize: 14,
                      backgroundColor: colors.background,
                      borderColor: `${colors.primary}40`,
                      borderStyle: 'solid',
                      color: colors.textMain,
                      height: 120,
                    }}
                  />
                </div>
              </div>

              <div style={{ padding: 10, gap: 8 }}>
                {/* Save & Exit - Primary action */}
                <button
                  style={{ ...commonStyles.secondaryButton, backgroundColor: colors.primary, cursor: 'pointer' }}
                  onClick={handleSaveAndExit}
                >
                  <span
                    style={{ ...commonStyles.secondaryButtonText, color: colors.isDark ? colors.background : colors.textMain, fontSize: 14 }}
                  >
                    Save & Exit
                  </span>
                </button>

                {/* Abandon Story - Exit without saving */}
                <button
                  style={{
                    ...commonStyles.secondaryButton,
                    backgroundColor: 'transparent',
                    borderWidth: 1,
                    borderColor: colors.textMuted,
                    borderStyle: 'solid',
                    marginTop: 16,
                    marginBottom: 40,
                    cursor: 'pointer',
                  }}
                  onClick={handleAbandonStory}
                >
                  <span
                    style={{
                      ...commonStyles.secondaryButtonText,
                      color: colors.textMuted,
                      fontSize: 13,
                    }}
                  >
                    Abandon Story (Exit Without Saving)
                  </span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
};

export default NarrativeUI;
